import Service from '@ember/service';
import { inject as service } from '@ember/service';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export default class SprintToolsService extends Service
{
	@service store;
	@service resultsAjax;

	/**
	 * Fetches results for an event and pushes them to the store
	 * @param {string} eventId - The ID of the event to fetch results for
	 */
	async fetchResultsForEvent(eventId)
	{
		this.store.unloadAll('result');

		var results = await this.resultsAjax.request("/events/"+eventId+"/results/individuals");
		for (const result of results)
		{
			this.store.pushPayload(result);
		}
	}

	/**
	 * Creates a class for a heat if it doesn't already exist
	 * @param {Object} destinationEvent - The destination event
	 * @param {Array} destinationClasses - Array of existing classes (will be mutated to add new class)
	 * @param {Object} classGroup - The class group for the heat
	 * @param {Object} result - The result containing ClassId info
	 * @returns {Object} The found or newly created class
	 */
	async createClassForHeat(destinationEvent, destinationClasses, classGroup, result)
	{
		var className = result.ClassId.get('displayName')+"*"+classGroup.name;
		var gender = result.ClassId.get('gender');

		//check if there is an existing class with the correct name and gender
		var existingClass = destinationClasses.find(
			dc => dc.name == className && dc.gender == gender
		);

		if(existingClass)
		{
			return existingClass;
		}

		//no existing class found, create one
		var newClass = this.store.createRecord('class',
		{
			name: className,
			gender: gender,
			EventId: destinationEvent,
			ClassGroupId: classGroup,
		});
		await newClass.save();
		// Add to destinationClasses so subsequent iterations can find it
		destinationClasses.push(newClass);
		return newClass;
	}

	/**
	 * Creates an entry for a heat with associated StartTimingRecord
	 * @param {Object} options - Options object
	 * @param {Object} options.destinationEvent - The destination event
	 * @param {Array} options.destinationEntries - Array of existing entries
	 * @param {Array} options.destinationClasses - Array of existing classes
	 * @param {Object} options.classGroup - The class group for the heat
	 * @param {Object} options.result - The result to create entry from
	 * @param {string} options.startTime - Start time in HH:mm:ss format
	 * @param {number} options.bib - The bib number (use result.newBib or result.bib)
	 * @returns {boolean} True if entry was created, false if entry already existed
	 */
	async createEntryForHeat({ destinationEvent, destinationEntries, destinationClasses, classGroup, result, startTime, bib })
	{
		var className = result.ClassId.get('displayName')+"*"+classGroup.name;

		//check if there is already an entry with this bib number
		var foundExistingEntry = destinationEntries.some(de => de.bib == bib);

		if(foundExistingEntry)
		{
			alert('Found existing entry for bib '+bib);
			return false;
		}

		//find the class for this entry
		var classForEntry = destinationClasses.find(
			dc => dc.name == className && dc.gender == result.ClassId.get('gender')
		);

		//return null on blank id's instead of ''
		var id2 = result.id2 === '' ? null : result.id2;
		var id3 = result.id3 === '' ? null : result.id3;
		var id1 = result.id1 === '' ? null : result.id1;

		var entry = this.store.createRecord('entry',
		{
			bib: bib,
			firstName: result.firstName,
			lastName: result.lastName,
			DOB: result.DOB,
			gender: result.ClassId.get('gender'),
			town: result.town,
			state: result.state,
			country: result.country,
			club: result.club,
			id2: id2,
			id3: id3,
			id1: id1,
			pts1: result.pts1,
			pts2: result.pts2,
			pts3: result.pts3,
			pts4: result.pts4,
			pts5: result.pts5,
			EventId: destinationEvent,
			ClassId: classForEntry,
		});

		var parsedStartTime = this.parseStartTime(startTime, destinationEvent.date);

		//save the entry and create a blank startTimingRecord
		await entry.save();
		await this.store.createRecord('StartTimingRecord',
		{
			EntryId: entry,
			assignedStartTime: new Date(parsedStartTime.toISOString()),
			participatesInAutoAssociation: true,
			EventId: destinationEvent,
		}).save();

		return true;
	}

	/**
	 * Parses a start time string and combines it with an event date
	 * @param {string} startTimeString - Time in HH:mm:ss format
	 * @param {Date} eventDate - The event date
	 * @returns {dayjs} The combined date/time
	 */
	parseStartTime(startTimeString, eventDate)
	{
		var parsedStartTime = dayjs(startTimeString, "HH:mm:ss");
		var eventDateDayjs = dayjs(eventDate);
		parsedStartTime = parsedStartTime.year(eventDateDayjs.year());
		parsedStartTime = parsedStartTime.month(eventDateDayjs.month());
		parsedStartTime = parsedStartTime.date(eventDateDayjs.date());
		return parsedStartTime;
	}

	/**
	 * Builds a CSV row for a heat result
	 * @param {Object} result - The result object
	 * @param {Object} classGroup - The class group
	 * @param {string} startTime - Start time in HH:mm:ss format
	 * @param {number} bib - The bib number to use (result.newBib or result.bib)
	 * @returns {Array} Array of values for the CSV row
	 */
	buildCSVRow(result, classGroup, startTime, bib)
	{
		var className = result.ClassId.get('displayName')+"*"+classGroup.get('name');
		return [
			bib,
			startTime,
			className,
			result.firstName,
			result.lastName,
			result.DOB,
			result.ClassId.get('gender'),
			result.town,
			result.state,
			result.country,
			result.club,
			result.id2,
			result.id3,
			result.id1,
			result.pts1,
			result.pts2,
			result.pts3,
			result.pts4,
			result.pts5
		];
	}

	/**
	 * Sort comparator for results by time, with tie-breaking by lowest SP points (USSA or FIS)
	 * Results with null times are sorted to the end
	 * @param {Object} a - First result
	 * @param {Object} b - Second result
	 * @returns {number} Sort comparison value (-1, 0, or 1)
	 */
	sortByTimeWithTieBreaker(a, b)
	{
		var aTime = a.totalTimeInMilliseconds;
		var bTime = b.totalTimeInMilliseconds;

		if (aTime != null && bTime != null)
		{
			if (aTime == bTime)
			{
				// Break ties by lowest of USSA or FIS SP points
				var aSP = Math.min(a.pts3, a.pts5);
				var bSP = Math.min(b.pts3, b.pts5);
				return aSP - bSP;
			}
			return aTime - bTime;
		}
		if (aTime != null) return -1;
		if (bTime != null) return 1;
		return 0;
	}

	/**
	 * Returns the standard CSV headers for heat startlists
	 * @returns {Array} Array containing the header row
	 */
	getCSVHeaders()
	{
		return [[
			"Bib","Start Time","Class","First Name","Last Name","Date of Birth","Gender","City","State","Country","Club","id2","id3","id1","pts1","pts2","pts3","pts4","pts5"
		]];
	}

	/**
	 * Creates all classes for a set of heat assignments
	 * @param {Object} destinationEvent - The destination event
	 * @param {Array} destinationClasses - Array of existing classes (will be mutated)
	 * @param {Object} classGroup - The class group for the heat
	 * @param {Array} assignments - Array of result assignments
	 */
	async createClassesForHeat(destinationEvent, destinationClasses, classGroup, assignments)
	{
		for (const result of assignments)
		{
			await this.createClassForHeat(destinationEvent, destinationClasses, classGroup, result);
		}
	}

	/**
	 * Creates all entries for a set of heat assignments
	 * @param {Object} destinationEvent - The destination event
	 * @param {Array} destinationEntries - Array of existing entries
	 * @param {Array} destinationClasses - Array of existing classes
	 * @param {Object} classGroup - The class group for the heat
	 * @param {Array} assignments - Array of result assignments
	 * @param {string} startTime - Start time in HH:mm:ss format
	 * @param {boolean} useNewBib - If true, use result.newBib; if false, use result.bib
	 */
	async createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, classGroup, assignments, startTime, useNewBib = true)
	{
		for (const result of assignments)
		{
			var bib = useNewBib ? result.newBib : result.bib;
			await this.createEntryForHeat({
				destinationEvent,
				destinationEntries,
				destinationClasses,
				classGroup,
				result,
				startTime,
				bib
			});
		}
	}
}
